import React, { useEffect } from 'react';

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <section className="min-h-screen bg-background-dark pt-32 pb-20 px-4 md:px-10">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-white text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            Terms & Conditions
          </h1>
          <p className="text-text-muted text-lg mb-6">Please read these terms carefully before using your services.</p>
          <div className="flex items-center justify-center gap-2 text-text-muted text-sm">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
            </svg>
            <span>Last updated: December 31, 2025</span>
          </div>
        </div>

        {/* Key Statement Banner */}
        <div className="bg-surface-dark border border-border-dark rounded-2xl p-6 mb-12 flex items-start gap-4">
          <div className="flex-shrink-0 size-12 rounded-full bg-primary/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-white text-base leading-relaxed">
              UPI Trackr is an expense analytics tool that analyzes transaction statements you choose to upload. We do not process payments or provide financial services.
            </p>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          <div className="flex flex-col items-start p-5 bg-surface-dark border border-border-dark rounded-xl">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <h3 className="text-white text-sm font-bold mb-1">Not a Bank</h3>
            <p className="text-text-muted text-xs">We are not a bank or payment provider.</p>
          </div>
          <div className="flex flex-col items-start p-5 bg-surface-dark border border-border-dark rounded-xl">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"/>
              </svg>
            </div>
            <h3 className="text-white text-sm font-bold mb-1">No Transaction Control</h3>
            <p className="text-text-muted text-xs">We do not execute or control UPI transactions.</p>
          </div>
          <div className="flex flex-col items-start p-5 bg-surface-dark border border-border-dark rounded-xl">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h3 className="text-white text-sm font-bold mb-1">Informational-Only</h3>
            <p className="text-text-muted text-xs">Analytics are informational purposes only.</p>
          </div>
          <div className="flex flex-col items-start p-5 bg-surface-dark border border-border-dark rounded-xl">
            <div className="size-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
              </svg>
            </div>
            <h3 className="text-white text-sm font-bold mb-1">Your Responsibility</h3>
            <p className="text-text-muted text-xs">You are responsible for the data you upload.</p>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-8 text-text-light">
          {/* 1. Acceptance of Terms */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">01. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By downloading, accessing, or using the UPI Trackr mobile application or website ("Service"), you agree to be bound by these Terms & Conditions ("Terms"). If you do not agree with any part of these terms, please discontinue use of the Service immediately.
            </p>
          </div>

          {/* 2. Nature of the Service */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">02. Nature of the Service</h2>
            <p className="mb-4 leading-relaxed">
              UPI Trackr is a personal finance analytics tool designed to help users visualize and understand their spending habits by processing UPI transaction statements provided by the user. Specifically, UPI Trackr is:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li><strong className="text-white">Analyzes PDF statements</strong> uploaded by you locally on your device.</li>
              <li><strong className="text-white">Categorizes expenses</strong> based on merchant data found in statements.</li>
            </ul>
            <p className="leading-relaxed mb-3">It is important to understand that UPI Trackr is not:</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-background-darker border border-border-dark rounded-lg p-4">
                <p className="text-white font-medium mb-2">✗ Not a Bank</p>
                <p className="text-text-muted text-sm">We do not hold funds or provide financial services.</p>
              </div>
              <div className="bg-background-darker border border-border-dark rounded-lg p-4">
                <p className="text-white font-medium mb-2">✗ Does not Hold Funds</p>
                <p className="text-text-muted text-sm">We do not hold or store any money on your behalf.</p>
              </div>
              <div className="bg-background-darker border border-border-dark rounded-lg p-4">
                <p className="text-white font-medium mb-2">✗ Does not Initiate Funds</p>
                <p className="text-text-muted text-sm">We do not access your bank accounts or initiate transactions.</p>
              </div>
              <div className="bg-background-darker border border-border-dark rounded-lg p-4">
                <p className="text-white font-medium mb-2">✗ Does not Validate Transactions</p>
                <p className="text-text-muted text-sm">We are not a ledger or clearing house.</p>
              </div>
            </div>
          </div>

          {/* 3. User Responsibilities */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">03. User Responsibilities</h2>
            <p className="mb-4 leading-relaxed">To use the Service, you represent and warrant that you:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Are at least 18 years of age.</li>
              <li>Have the legal authority to share the transaction statements you upload.</li>
              <li>Will not use the app for any fraudulent or illegal activity.</li>
              <li>Are solely responsible for maintaining the confidentiality of your device and data.</li>
            </ul>
          </div>

          {/* 4. Statement Import & Data Accuracy */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">04. Statement Import & Data Accuracy</h2>
            <p className="mb-4 leading-relaxed">
              The Service relies entirely on the data contained in the PDF statements you import. We do not verify the authenticity or accuracy of these documents with your bank.
            </p>
            <div className="bg-surface-dark border border-primary/30 rounded-lg p-5 mb-4">
              <p className="text-white font-medium mb-2">Parsing Accuracy:</p>
              <p className="text-text-muted text-sm leading-relaxed">
                While we strive for accuracy in parsing transaction data from PDF statements, variations in bank formats may lead to incorrect parsing. UPI Trackr is not liable for any inaccuracies. All transaction data and calculations will be imported correctly or categorized perfectly.
              </p>
            </div>
            <p className="leading-relaxed">
              Users should verify important financial figures against their official bank records.
            </p>
          </div>

          {/* 5. No Financial Advice */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">05. No Financial Advice</h2>
            <p className="leading-relaxed">
              The analytics, charts, and insights provided by UPI Trackr are for informational purposes only. They do not constitute financial, tax, or legal advice. You should not act, or neglect to act, on any legal or financial matter based solely on app-generated insights. You should consult with a qualified professional for investment or financial decisions.
            </p>
          </div>

          {/* 6. Data Storage & Backup */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">06. Data Storage & Backup</h2>
            <p className="mb-4 leading-relaxed">
              <strong className="text-white">Local Storage:</strong> UPI Trackr operates in "privacy-first" mode. The data parsed from your statements is stored locally on your device in encrypted format. We do not upload your financial transaction data to external servers.
            </p>
            <p className="mb-4 leading-relaxed">
              <strong className="text-white">Backups:</strong> Any backup functionality provided (e.g., via Google Drive) is managed by the user. You are responsible for managing your backups. UPI Trackr is not liable for data loss due to device failure, uninstallation, or loss of access to cloud storage accounts.
            </p>
          </div>

          {/* 7. Third-Party Services */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">07. Third-Party Services</h2>
            <p className="mb-4 leading-relaxed">
              The Service may interact with third-party services, such as file pickers for PDF selection or cloud storage for backups. We do not control these services.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We are not responsible for the availability or uptime of third-party services.</li>
              <li>We are not liable for any issues arising from your use of third-party providers.</li>
            </ul>
          </div>

          {/* 8. Limitation of Liability */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">08. Limitation of Liability</h2>
            <p className="mb-4 leading-relaxed">
              To the maximum extent permitted by applicable law, UPI Trackr and its developers shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-background-darker border border-border-dark rounded-lg p-4">
                <p className="text-white font-medium text-sm mb-2">Service Use</p>
                <p className="text-text-muted text-xs">Your access to or use of (or inability to access or use) the Service.</p>
              </div>
              <div className="bg-background-darker border border-border-dark rounded-lg p-4">
                <p className="text-white font-medium text-sm mb-2">Inaccuracy</p>
                <p className="text-text-muted text-xs">Any errors or omissions in the analysis of your statements.</p>
              </div>
            </div>
          </div>

          {/* 9. Termination */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">09. Termination</h2>
            <p className="mb-4 leading-relaxed">
              We reserve the right to suspend or terminate your access to the Service at our sole discretion, without notice or liability, if you violate these Terms, or if we are required to do so by law or in the interest of these Terms.
            </p>
          </div>

          {/* 10. Changes to Terms */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">10. Changes to Terms</h2>
            <p className="leading-relaxed">
              We may update these Terms from time to time. If we make material changes, we will notify you through the Service or by email (where applicable). Continued use of the Service after updated constitutes acceptance of the new Terms.
            </p>
          </div>

          {/* 11. Governing Law */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">11. Governing Law</h2>
            <p className="leading-relaxed">
              These Terms shall be governed and interpreted in accordance with the laws of India, without regard to its conflict of law provisions.
            </p>
          </div>

          {/* 12. Contact Information */}
          <div>
            <h2 className="text-white text-2xl font-bold mb-4">12. Contact Information</h2>
            <p className="mb-4 leading-relaxed">
              If you have any questions about these Terms, please contact us:
            </p>
            <a 
              href="mailto:itsankar2005@gmail.com"
              className="inline-flex items-center gap-2 bg-primary text-background-dark px-6 py-3 rounded-full font-semibold hover:bg-opacity-90 transition-opacity"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              itsankar2005@gmail.com
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TermsAndConditions;
